import { type FC, useState, useEffect } from 'react';
import { type Control, Controller, useWatch } from 'react-hook-form-latest';
import { RiCheckLine, RiEyeLine, RiEyeOffLine, RiErrorWarningLine } from 'react-icons/ri';
import Switch from '@/components/UI/FormElements/Switch';
import { TeamThemeService } from '@/support/TeamThemeService';
import type { Color } from '@/model/Common.model';
import ColorInput from './ColorInput';
import { type TeamConfigFormValues, isValidHexColor, wcagContrastRatio, passesWcagAA } from './TeamConfig.utils';
import styles from './TeamConfig.module.scss';

interface ThemeCardProps {
  control: Control<TeamConfigFormValues>;
  testId?: string;
}

/**
 * Config card for managing the tenant sidebar theme.
 * Shows color pickers for primary/on_primary with live sidebar preview and WCAG contrast checking.
 * When inactive, the form is visible but disabled.
 */
const ThemeCard: FC<ThemeCardProps> = ({ control, testId = 'theme-card' }) => {
  const [previewing, setPreviewing] = useState(false);

  const active = useWatch({ control, name: 'theme.active' });
  const primary = useWatch({ control, name: 'theme.value.primary' });
  const onPrimary = useWatch({ control, name: 'theme.value.on_primary' });

  const bothValid = isValidHexColor(primary) && isValidHexColor(onPrimary);

  useEffect(() => {
    if (previewing && bothValid) {
      TeamThemeService.preview({ primary: primary as Color, on_primary: onPrimary as Color });
    }
  }, [previewing, primary, onPrimary, bothValid]);

  useEffect(() => {
    return () => {
      if (previewing) {
        TeamThemeService.applyFromCache();
      }
    };
  }, [previewing]);

  useEffect(() => {
    if (!active && previewing) {
      TeamThemeService.applyFromCache();
      setPreviewing(false);
    }
  }, [active, previewing]);

  const handlePreviewToggle = () => {
    if (previewing) {
      TeamThemeService.applyFromCache();
      setPreviewing(false);
    } else {
      setPreviewing(true);
    }
  };

  const contrastRatio = bothValid ? wcagContrastRatio(primary, onPrimary) : null;
  const contrastLevel = contrastRatio === null ? 'disabled' : passesWcagAA(contrastRatio) ? 'pass' : 'fail';

  return (
    <div className={styles.card} data-testid={testId}>
      <div className={styles.cardHeader}>
        <div>
          <span className="fw6 f5">Theme</span>
          <p className="f6 dark-gray lh-copy mt2 mb0">
            Controls the appearance of the navigation sidebar across this tenant. <strong>primary</strong> sets the
            sidebar background; <strong>on_primary</strong> sets the icon and text colour.
          </p>
        </div>
        <div className="flex items-center" style={{ gap: '0.75rem' }}>
          <span className="f6 mid-gray">{active ? 'Active' : 'Inactive'}</span>
          <Controller
            name="theme.active"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Switch value={value} onChange={onChange} testId={`${testId}-active-switch`} />
            )}
          />
        </div>
      </div>

      <div className={`${styles.cardBody} ${!active ? styles.cardBodyDisabled : ''}`} data-testid={`${testId}-body`}>
        <div className="flex items-end" style={{ gap: '2rem' }}>
          <Controller
            name="theme.value.primary"
            control={control}
            rules={{ validate: isValidHexColor }}
            render={({ field: { onChange, value }, fieldState: { invalid } }) => (
              <ColorInput
                value={value}
                onChange={onChange}
                fieldName="primary"
                label="Main brand color"
                error={invalid}
                disabled={!active}
                testId={`${testId}-primary`}
              />
            )}
          />
          <Controller
            name="theme.value.on_primary"
            control={control}
            rules={{ validate: isValidHexColor }}
            render={({ field: { onChange, value }, fieldState: { invalid } }) => (
              <ColorInput
                value={value}
                onChange={onChange}
                fieldName="on_primary"
                label="Content color on primary"
                error={invalid}
                disabled={!active}
                testId={`${testId}-on-primary`}
              />
            )}
          />
          {active && (
            <span
              role="button"
              tabIndex={0}
              className={`${styles.previewLink} pointer flex items-center`}
              onClick={handlePreviewToggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePreviewToggle();
                }
              }}
              data-testid={`${testId}-preview-toggle`}
            >
              {previewing ? <RiEyeLine size={18} /> : <RiEyeOffLine size={18} />}
              <span className="ml1">Preview</span>
            </span>
          )}
        </div>

        <div
          className={`${styles.contrastBadge} ${styles[`contrast_${contrastLevel}`]}`}
          data-testid={`${testId}-contrast-badge`}
        >
          {contrastRatio === null ? (
            <>— Contrast unavailable</>
          ) : (
            <>
              Contrast {contrastRatio.toFixed(1)}:1{' '}
              {contrastLevel === 'pass' ? (
                <RiCheckLine size={14} className="green" />
              ) : (
                <RiErrorWarningLine size={14} className="red" />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThemeCard;
